// Global Instructions Rule Applied!
//
// Period Highlights — HTTP service (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 1). Talks to the Python backend only (CLAUDE.md — never Supabase
// directly); every URL comes from API_ROUTES; every response is validated with
// its Zod model before it reaches UI state.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  HighlightCloneDraftResult,
  HighlightHistoryManifest,
  HighlightRefreshMetricsResult,
  HighlightSetDetail,
  HighlightSetFilterOptions,
  HighlightBenchManifest,
  HighlightRewordingManifest,
  RewordedHighlightItems,
  RewordingRegenerateResult,
  RewordingSignOffResult,
  HighlightSetRecord,
  HighlightSetStaleness,
  HighlightWithdrawSignOffResult,
  validateHighlightCloneDraftResult,
  validateHighlightBenchManifest, validateHighlightHistoryManifest, validateHighlightRewordingManifest,
  validateRewordedHighlightItems, validateRewordingRegenerateResult, validateRewordingSignOffResult,
  validateHighlightRefreshMetricsResult,
  validateHighlightSetDetail,
  validateHighlightSetFilterOptions,
  validateHighlightSetRows,
  validateHighlightSetStaleness,
  validateHighlightWithdrawSignOffResult
} from '../../../state_manager/data_models/period_highlights_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// The backend's own sentence, never a bare status line (design patterns:
// "Feedback on a blocked action").
const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await readJsonBody<{ error?: string; message?: string }>(response)
  return body?.message || body?.error || `${fallback}: ${response.status} ${response.statusText}`
}

const request = async (url: string, init: RequestInit, accessToken: string | null, fallback: string): Promise<any> => {
  const response = await fetch(url, { ...init, headers: jsonHeaders(accessToken) })
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(await readError(response, fallback))
  }
  return response.json()
}

export interface ListResult<T> {
  data: T[]
  has_more: boolean
  count: number
}

export interface HighlightSetsQuery {
  filters?: Array<{ key: string; value: unknown }>
  sorting?: Array<{ key: string; direction: 'asc' | 'desc' }>
  page?: number
  page_size?: number
}

export const fetchHighlightSets = async (query: HighlightSetsQuery, accessToken: string | null): Promise<ListResult<HighlightSetRecord>> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.LIST)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: query }) }, accessToken, 'Failed to load period highlights')
  return { data: validateHighlightSetRows(json.data ?? []), has_more: Boolean(json.has_more), count: Number(json.count ?? 0) }
}

export const fetchHighlightSetFilterOptions = async (accessToken: string | null): Promise<HighlightSetFilterOptions> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.FILTER_OPTIONS)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to load filter options')
  return validateHighlightSetFilterOptions(json.data ?? {})
}

export const fetchHighlightSetDetail = async (setId: string, accessToken: string | null): Promise<HighlightSetDetail> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.DETAIL, { setId })
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load the highlight set')
  return validateHighlightSetDetail(json.data ?? {})
}

export const fetchCompanyHighlightSets = async (accountId: string, accessToken: string | null): Promise<HighlightSetRecord[]> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.COMPANY, { accountId })
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load company highlights')
  return validateHighlightSetRows(json.data ?? [])
}

export interface GenerateHighlightsPayload {
  scope_type: 'fund' | 'fund_group' | 'company' | 'lp_account'
  fund_id?: string
  fund_group_id?: string
  account_id?: string
  period_start: string
  period_end: string
  force?: boolean
}

export const fetchHighlightSetStaleness = async (setId: string, accessToken: string | null): Promise<HighlightSetStaleness> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.STALENESS, { setId })
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to check the set')
  return validateHighlightSetStaleness(json.data ?? {})
}

export interface GenerateHistoryPayload {
  scope_types: Array<'fund' | 'fund_group' | 'lp_account'>
  quarters: number
  dry_run: boolean
  stagger_minutes?: number
  force?: boolean
  /** Under force: leave the keys whose version was signed off or hand-edited (backlog 2.25). */
  keep_reviewed?: boolean
  /** Of one kind: narrows the run to those subjects (Follower Agents → Backfill;
   *  one subject since PR 6, several since 2026-10-29). Empty / absent = the fleet. */
  fund_ids?: string[]
  fund_group_ids?: string[]
  account_ids?: string[]
}

export const generateHighlightsHistory = async (payload: GenerateHistoryPayload, accessToken: string | null): Promise<HighlightHistoryManifest> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.GENERATE_HISTORY)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to plan the history generation')
  return validateHighlightHistoryManifest(json.data ?? {})
}

export const generateHighlights = async (payload: GenerateHighlightsPayload, accessToken: string | null): Promise<any> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.GENERATE)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to start generation')
  return json.data
}

export const approveHighlightSet = async (setId: string, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.APPROVE, { setId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to approve the set')
}

export const rejectHighlightSet = async (setId: string, reason: string, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REJECT, { setId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: { reason } }) }, accessToken, 'Failed to reject the set')
}

/** Restore as new version (backlog 2.23): a superseded version becomes a new draft. */
export const cloneHighlightSetDraft = async (setId: string, accessToken: string | null): Promise<HighlightCloneDraftResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.CLONE_DRAFT, { setId })
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to create a draft')
  return validateHighlightCloneDraftResult(json.data ?? {})
}

export const reorderHighlightItems = async (setId: string, itemIds: string[], accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REORDER, { setId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: { item_ids: itemIds } }) }, accessToken, 'Failed to reorder the items')
}

/** The runners-up backfill (2026-10-29): the history form's shape without
 *  force / keep_reviewed — a model-free pass per instance and quarter whose key
 *  has a current approved set. */
export type GenerateBenchPayload = Omit<GenerateHistoryPayload, 'force' | 'keep_reviewed'>

export const generateHighlightsBench = async (payload: GenerateBenchPayload, accessToken: string | null): Promise<HighlightBenchManifest> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.BENCH_BACKFILL)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to plan the runners-up backfill')
  return validateHighlightBenchManifest(json.data ?? {})
}

/** The agent's rewording of stored highlight texts (owner 2026-11-05): a
 *  dry-run manifest of the current sets whose selected items break the house
 *  rules, then one queued stage 'reword' run (one model call) per set. */
export interface GenerateRewordingPayload {
  dry_run: boolean
  scope_types?: string[]
  stagger_minutes?: number
  fund_ids?: string[]
  fund_group_ids?: string[]
  account_ids?: string[]
}

export const generateHighlightsRewording = async (payload: GenerateRewordingPayload, accessToken: string | null): Promise<HighlightRewordingManifest> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REWORDING)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to plan the rewording')
  return validateHighlightRewordingManifest(json.data ?? {})
}

/** The review list of reworded highlights (Follower Agents), with the caps its editor enforces. */
export const fetchRewordedHighlightItems = async (accessToken: string | null): Promise<RewordedHighlightItems> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REWORDING_ITEMS)
  const json = await request(url, { method: 'GET' }, accessToken, 'Failed to load the reworded highlights')
  return validateRewordedHighlightItems(json.data ?? {})
}

/** Sign off these rewordings; a set whose only change since its sign-off is the rewording is signed off again. */
export const signOffRewordedHighlightItems = async (itemIds: string[], accessToken: string | null): Promise<RewordingSignOffResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REWORDING_SIGN_OFF)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: { item_ids: itemIds } }) }, accessToken, 'Failed to sign off the rewordings')
  return validateRewordingSignOffResult(json.data ?? {})
}

/** The agent writes these reworded highlights again: one queued run per set. */
export const regenerateRewordedHighlightItems = async (itemIds: string[], accessToken: string | null): Promise<RewordingRegenerateResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REWORDING_REGENERATE)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: { item_ids: itemIds } }) }, accessToken, 'Failed to queue the rewording')
  return validateRewordingRegenerateResult(json.data ?? {})
}

/** Regenerate ONE set's narrative over its current selection (backlog
 *  2.21): queued like Reformulate, one narrative-only model call, written in
 *  place (the revision bumps; a signed-off set re-asks). */
export const regenerateHighlightSetNarrative = async (setId: string, accessToken: string | null): Promise<any> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.NARRATIVE_REGENERATE, { setId })
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to queue the narrative regeneration')
  return json.data
}

/** Add the runners-up to ONE set: queued like Reformulate, model-free. */
export const benchHighlightSet = async (setId: string, accessToken: string | null): Promise<any> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.BENCH, { setId })
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to queue the runners-up')
  return json.data
}

export const reformulateHighlightSet = async (setId: string, accessToken: string | null): Promise<any> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REFORMULATE, { setId })
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to start a reformulation')
  return json.data
}

export interface HighlightItemEditPayload {
  headline?: string
  body?: string | null
  short_text?: string | null
  status?: 'included' | 'excluded'
}

export interface HighlightEditResult {
  warnings?: string[]
  highlight_sets__revision?: number | null
  [key: string]: unknown
}

export const editHighlightItem = async (itemId: string, payload: HighlightItemEditPayload, accessToken: string | null): Promise<HighlightEditResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.ITEM, { itemId })
  const json = await request(url, { method: 'PATCH', body: JSON.stringify({ data: payload }) }, accessToken, 'Failed to save the item')
  return (json.data ?? {}) as HighlightEditResult
}

export const editHighlightSetNarrative = async (setId: string, narrative: string | null, accessToken: string | null): Promise<HighlightEditResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.NARRATIVE, { setId })
  const json = await request(url, { method: 'PATCH', body: JSON.stringify({ data: { narrative } }) }, accessToken, 'Failed to save the narrative')
  return (json.data ?? {}) as HighlightEditResult
}

export const signOffHighlightSet = async (setId: string, accessToken: string | null): Promise<void> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.SIGN_OFF, { setId })
  await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to sign off the set')
}

/** Take a sign-off back (backlog 2.22): the set reads "not reviewed" again. */
export const withdrawHighlightSetSignOff = async (setId: string, accessToken: string | null): Promise<HighlightWithdrawSignOffResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.WITHDRAW_SIGN_OFF, { setId })
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to withdraw the sign-off')
  return validateHighlightWithdrawSignOffResult(json.data ?? {})
}

export const refreshHighlightSetMetrics = async (setId: string, accessToken: string | null): Promise<HighlightRefreshMetricsResult> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PERIOD_HIGHLIGHTS.REFRESH_METRICS, { setId })
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to refresh the figures')
  return validateHighlightRefreshMetricsResult(json.data ?? {})
}
